import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { LuArrowLeft, LuKeyRound, LuLoaderCircle, LuMousePointerClick, LuRotateCcw, LuSave, LuSearch, LuSearchX, LuX } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import SegmentedControl from '../../../components/ui/SegmentedControl';
import { GLASS_FIELD } from '../../../components/ui/glass';
import { getMenuIcon } from '../../../components/shell/menuConfig';
import useAccessText from '../accessText';
import { isActiveEmployee, pageState } from '../accessUtils';
import { AccessSwitch, PassiveTag, PersonAvatar, RoleChip, STATE_META, StateTag } from './accessBits';
import { CARD_SURFACE } from './EmployeeList';

const LEGEND = ['role', 'special', 'granted', 'none'];

const SummaryRing = ({ value, total, hint, caption }) => {
  const size = 52;
  const r = size / 2 - 5;
  const c = 2 * Math.PI * r;
  const share = total > 0 ? value / total : 0;
  return (
    <span title={hint} className="tw-flex tw-items-center tw-gap-2.5">
      <span className="tw-relative tw-grid tw-h-[52px] tw-w-[52px] tw-shrink-0 tw-place-items-center">
        <svg viewBox={`0 0 ${size} ${size}`} className="tw-absolute tw-inset-0 -tw-rotate-90" aria-hidden="true">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth="5" stroke="currentColor" className="tw-text-slate-200 dark:tw-text-white/10" />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth="5" stroke="#14b8a6" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - share)} style={{ transition: 'stroke-dashoffset 0.4s ease' }} />
        </svg>
        <span className="tw-text-sm tw-font-bold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{value}</span>
      </span>
      <span className="tw-text-xs tw-font-medium tw-leading-tight tw-text-slate-500 dark:tw-text-slate-400">{caption}</span>
    </span>
  );
};

SummaryRing.propTypes = { value: PropTypes.number.isRequired, total: PropTypes.number.isRequired, hint: PropTypes.string, caption: PropTypes.string };

/**
 * The selected employee's pages (right column): who they are, the password
 * change, how many pages they can open, a search and Tümü / Erişimi olan /
 * olmayan filter, the pages by menu with a switch each (locked when the role
 * or the code gives the page) and the save bar while the draft is unsaved.
 */
const AccessEditor = ({
  employee, roles, online, allPages, draftGrants, accessibleCount,
  pageSearch, onPageSearch, pageFilter, onPageFilter,
  onToggle, onToggleGroup, onPassword, hasChanges, changeCount, saving, onReset, onSave,
  showBack, onBack, className = '',
}) => {
  const { t, label, pageTitle, groupTitle, roleLabel } = useAccessText();

  const groups = useMemo(() => {
    if (!employee) return [];
    const term = pageSearch.trim().toLocaleLowerCase('tr-TR');
    const result = [];
    const byGroup = {};
    // The filter looks at the saved access, so a row switched in the draft stays in view
    const saved = Array.isArray(employee.pageAccess) ? employee.pageAccess : [];
    allPages.forEach((page) => {
      const state = pageState(page, draftGrants, employee.eMail, roles);
      const savedState = pageState(page, saved, employee.eMail, roles);
      if (pageFilter === 'on' && savedState === 'none') return;
      if (pageFilter === 'off' && savedState !== 'none') return;
      if (term) {
        const haystack = [page.title, pageTitle(page), page.path, page.group, groupTitle(page.groupPath, page.group)].join(' ').toLocaleLowerCase('tr-TR');
        if (!haystack.includes(term)) return;
      }
      if (!byGroup[page.group]) {
        byGroup[page.group] = { group: page.group, groupPath: page.groupPath, pages: [] };
        result.push(byGroup[page.group]);
      }
      byGroup[page.group].pages.push({ page, state });
    });
    return result;
  }, [employee, allPages, draftGrants, roles, pageSearch, pageFilter, pageTitle, groupTitle]);

  if (!employee) {
    return (
      <section aria-label={label('editor.label')} className={`${CARD_SURFACE} tw-items-center tw-justify-center tw-p-8 tw-text-center ${className}`}>
        <span className="tw-grid tw-h-16 tw-w-16 tw-place-items-center tw-rounded-3xl tw-bg-teal-500/10 tw-text-teal-600 dark:tw-text-teal-300">
          <LuMousePointerClick aria-hidden="true" className="tw-h-8 tw-w-8" />
        </span>
        <h2 className="tw-mt-4 tw-text-lg tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label('editor.placeholderTitle')}</h2>
        <p className="tw-mt-1 tw-max-w-sm tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('editor.placeholderText')}</p>
      </section>
    );
  }

  const passive = !isActiveEmployee(employee);

  return (
    <section aria-label={label('editor.label')} className={`${CARD_SURFACE} ${className}`}>
      <div className="tw-flex tw-flex-col tw-gap-3 tw-border-b tw-border-slate-200/70 tw-p-3 sm:tw-flex-row sm:tw-items-center sm:tw-p-4 dark:tw-border-white/10">
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-3">
          {showBack && (
            <button type="button" onClick={onBack} aria-label={label('editor.back')} className="tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-slate-900/[0.05] tw-text-slate-600 hover:tw-bg-slate-900/10 dark:tw-bg-white/[0.07] dark:tw-text-slate-200">
              <LuArrowLeft aria-hidden="true" className="tw-h-4 tw-w-4" />
            </button>
          )}
          <PersonAvatar employee={employee} online={online} passive={passive} size="avatarXl" />
          <div className="tw-min-w-0">
            <h2 className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-text-lg tw-font-semibold tw-text-slate-900 dark:tw-text-white">
              <span className="tw-truncate">{employee.eName} {employee.eSurname}</span>
              {passive && <PassiveTag />}
            </h2>
            <p className="tw-truncate tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{employee.eMail || '—'}</p>
            <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-gap-1">
              {roles.map((role) => <RoleChip key={role}>{roleLabel(role)}</RoleChip>)}
            </div>
          </div>
        </div>
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 sm:tw-justify-end">
          <SummaryRing value={accessibleCount} total={allPages.length} hint={label('editor.summaryHint')} caption={label('editor.summary', { total: allPages.length })} />
          <Button size="sm" onClick={onPassword} title={label('editor.passwordHint')}>
            <LuKeyRound aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-amber-600 dark:tw-text-amber-400" />
            {label('editor.password')}
          </Button>
        </div>
      </div>

      <div className="tw-space-y-2.5 tw-border-b tw-border-slate-200/70 tw-p-3 sm:tw-px-4 dark:tw-border-white/10">
        <div className="tw-flex tw-flex-col tw-gap-2 md:tw-flex-row md:tw-items-center">
          <label className="tw-relative tw-block md:tw-max-w-xs md:tw-flex-1">
            <span className="tw-sr-only">{label('editor.search')}</span>
            <LuSearch aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
            <input type="search" value={pageSearch} onChange={(event) => onPageSearch(event.target.value)} placeholder={label('editor.search')} className={`${GLASS_FIELD} tw-pl-10 tw-pr-10`} />
            {pageSearch && (
              <button type="button" onClick={() => onPageSearch('')} aria-label={t('common.clearSearch')} className="tw-absolute tw-right-2 tw-top-1/2 tw-grid tw-h-7 tw-w-7 -tw-translate-y-1/2 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:hover:tw-bg-white/10 dark:hover:tw-text-white">
                <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
              </button>
            )}
          </label>
          <SegmentedControl
            size="sm"
            layoutId="page-access-page-filter"
            label={label('editor.filterLabel')}
            value={pageFilter}
            onChange={onPageFilter}
            options={['all', 'on', 'off'].map((value) => ({ value, label: label(`editor.filter.${value}`) }))}
          />
        </div>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1 tw-px-1">
          {LEGEND.map((key) => (
            <span key={key} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
              <span aria-hidden="true" className={`tw-h-2 tw-w-2 tw-rounded-full ${STATE_META[key].dot}`} />
              {label(`editor.legend.${key}`)}
            </span>
          ))}
        </div>
      </div>

      <div className="tw-min-h-0 tw-flex-1 tw-space-y-3 tw-overflow-y-auto tw-p-3 sm:tw-p-4 glass-scrollbar">
        {groups.length === 0 && (
          <p className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-10 tw-text-center tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
            <LuSearchX aria-hidden="true" className="tw-h-6 tw-w-6 tw-text-slate-400" />
            {label('editor.noMatch')}
          </p>
        )}
        {groups.map((section) => {
          const Icon = getMenuIcon(section.groupPath);
          const onCount = section.pages.filter(({ state }) => state !== 'none').length;
          const free = section.pages.filter(({ state }) => state === 'none' || state === 'granted');
          const allFreeOn = free.length > 0 && free.every(({ state }) => state === 'granted');
          return (
            <div key={section.group} className="tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/60 dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
              <div className="tw-flex tw-items-center tw-gap-2.5 tw-border-b tw-border-slate-200/70 tw-bg-slate-50/80 tw-px-3.5 tw-py-2.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                <span className="tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-lg tw-bg-teal-500/10 tw-text-teal-700 dark:tw-text-teal-300">
                  <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
                </span>
                <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{groupTitle(section.groupPath, section.group)}</span>
                <span className="tw-shrink-0 tw-text-xs tw-font-semibold tw-tabular-nums tw-text-slate-400">{onCount}/{section.pages.length}</span>
                {free.length > 0 && (
                  <button
                    type="button"
                    onClick={() => onToggleGroup(section.pages.map(({ page }) => page))}
                    title={label('editor.groupHint')}
                    className="tw-shrink-0 tw-rounded-full tw-bg-transparent tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-teal-700 hover:tw-bg-teal-500/10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-text-teal-300"
                  >
                    {allFreeOn ? label('editor.removeAll') : label('editor.addAll')}
                  </button>
                )}
              </div>
              <ul className="tw-divide-y tw-divide-slate-100 dark:tw-divide-white/[0.06]">
                {section.pages.map(({ page, state }) => {
                  const locked = state === 'role' || state === 'special';
                  const on = state !== 'none';
                  return (
                    <li key={page.path} className={`tw-flex tw-items-center tw-gap-3 tw-px-3.5 tw-py-2.5 ${on ? '' : 'tw-bg-transparent'}`}>
                      <span className="tw-min-w-0 tw-flex-1">
                        <span className={`tw-block tw-truncate tw-text-sm tw-font-medium ${on ? 'tw-text-slate-900 dark:tw-text-white' : 'tw-text-slate-500 dark:tw-text-slate-400'}`}>{pageTitle(page)}</span>
                        <span className="tw-block tw-truncate tw-font-mono tw-text-[11px] tw-text-slate-400 dark:tw-text-slate-500">{page.path}</span>
                      </span>
                      <StateTag state={state} />
                      <AccessSwitch
                        on={on}
                        locked={locked}
                        title={locked ? label('editor.locked') : on ? label('editor.remove') : label('editor.add')}
                        onToggle={() => onToggle(page)}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      {hasChanges && (
        <div className="tw-sticky tw-bottom-3 tw-z-10 tw-m-3 tw-mt-0 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-2xl tw-border tw-border-amber-400/40 tw-bg-amber-50/95 tw-px-3.5 tw-py-2.5 tw-shadow-lg tw-backdrop-blur sm:tw-mx-4 lg:tw-static lg:tw-mb-4 dark:tw-border-amber-400/25 dark:tw-bg-[#3a3324]/95">
          <span className="tw-min-w-0 tw-flex-1 tw-text-sm tw-font-semibold tw-text-amber-900 dark:tw-text-amber-100">{label('editor.unsaved', { count: changeCount })}</span>
          <Button size="sm" variant="ghost" onClick={onReset} disabled={saving}>
            <LuRotateCcw aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('editor.reset')}
          </Button>
          <Button size="sm" variant="primary" onClick={onSave} disabled={saving}>
            {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {saving ? label('editor.saving') : label('editor.save')}
          </Button>
        </div>
      )}
    </section>
  );
};

AccessEditor.propTypes = {
  employee: PropTypes.object,
  roles: PropTypes.arrayOf(PropTypes.string).isRequired,
  online: PropTypes.bool,
  allPages: PropTypes.arrayOf(PropTypes.object).isRequired,
  draftGrants: PropTypes.arrayOf(PropTypes.string).isRequired,
  accessibleCount: PropTypes.number.isRequired,
  pageSearch: PropTypes.string.isRequired,
  onPageSearch: PropTypes.func.isRequired,
  pageFilter: PropTypes.string.isRequired,
  onPageFilter: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onToggleGroup: PropTypes.func.isRequired,
  onPassword: PropTypes.func.isRequired,
  hasChanges: PropTypes.bool,
  changeCount: PropTypes.number,
  saving: PropTypes.bool,
  onReset: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  showBack: PropTypes.bool,
  onBack: PropTypes.func,
  className: PropTypes.string,
};

export default AccessEditor;
